import * as React from 'react';

/**
 * This exists to be able to be able to load a component based on a trigger, but without having the component unmount when that trigger is reset.
 * Eg. If we do it like this:
 *
 *   <React.Suspense>
 *     {isOpen && <Flyout />}
 *   </React.Suspense>
 *
 * Then when the isOpen returns to false the flyout will unmount. This is fine for some cases, but unnecessary for most.
 *
 * So, using ExciteSuspense instead looks like this:
 *
 *    <ExciteSuspense loadComponent={isOpen}>
 *      <Flyout />
 *    </ExciteSuspense>
 *
 * This will keep the Flyout mounted even though isOpen returns to false.
 */
export function ExciteSuspense({
  loadComponent = true,
  fallback,
  children,
}: React.PropsWithChildren<{ loadComponent?: boolean; fallback?: React.ReactNode }>) {
  const [hasLoaded, setHasLoaded] = React.useState(loadComponent);

  React.useEffect(() => {
    if (loadComponent) {
      setHasLoaded(true);
    }
  }, [loadComponent]);

  return <React.Suspense fallback={fallback}>{hasLoaded && children}</React.Suspense>;
}
